import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { Check, Clock3, History } from "lucide-react";
import { motion } from "motion/react";
import { ActionChip } from "./ActionChip";
import { palette } from "../theme";
import { toTimeStr } from "../util/time";
import { fullName } from "../core";
import type { ActionType, Comment } from "../generated/core-types";
import { ValidationActions } from "../validation/ValidationActions";

interface CommentCardProps {
  comment: Comment;
  onValidate: (action: ActionType) => void;
  onShowHistory?: () => void;
  highlightMatch?: boolean;
  selected?: boolean;
}

export function CommentCard({
  comment,
  onValidate,
  onShowHistory,
  highlightMatch = false,
  selected = false,
}: CommentCardProps): JSX.Element {
  const leftBorder = highlightMatch
    ? `3px solid ${palette.sage}`
    : `1px solid ${palette.border}`;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 32, transition: { duration: 0.18 } }}
      transition={{ duration: 0.22, ease: [0.2, 0, 0.2, 1] }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto auto 1fr auto",
          alignItems: "center",
          gap: 2,
          padding: "14px 18px",
          backgroundColor: selected ? palette.sageBg : palette.surface,
          border: leftBorder,
          borderTop: `1px solid ${palette.border}`,
          borderRight: `1px solid ${palette.border}`,
          borderBottom: `1px solid ${palette.border}`,
          borderRadius: 8,
          transition: "background-color 140ms ease, border-color 140ms ease",
          "&:hover": {
            borderTopColor: palette.ink,
            borderRightColor: palette.ink,
            borderBottomColor: palette.ink,
            borderLeftColor: highlightMatch ? palette.sage : palette.ink,
          },
          "&:hover .row-actions": { opacity: 1 },
        }}
      >
        {/* Action chip + item number */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", minWidth: 110 }}>
          <ActionChip action={comment.action} />
          <Box
            component="span"
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: "0.78rem",
              color: palette.inkSoft,
              fontVariantNumeric: "tabular-nums",
              letterSpacing: "0.04em",
            }}
          >
            sản phẩm {String(comment.item).padStart(2, "0")}
          </Box>
        </Box>

        {/* User identity */}
        <Box sx={{ minWidth: 180, maxWidth: 220 }}>
          <Box
            sx={{
              fontFamily: '"Fraunces Variable", Georgia, serif',
              fontStyle: "italic",
              fontWeight: 500,
              fontSize: "1.02rem",
              color: palette.ink,
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {fullName(comment.user)}
          </Box>
          <Box
            sx={{
              mt: "4px",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: "0.72rem",
              color: palette.inkSoft,
              fontVariantNumeric: "tabular-nums",
              letterSpacing: "0.02em",
            }}
          >
            <Clock3 size={11} strokeWidth={1.5} />
            {toTimeStr(comment.timestamp)}
          </Box>
        </Box>

        {/* Comment body */}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: '"Geist", system-ui, sans-serif',
              fontSize: "1.02rem",
              lineHeight: 1.45,
              color: palette.ink,
              wordBreak: "break-word",
            }}
          >
            <Box component="span" sx={{ color: palette.inkSoft, mr: "2px" }}>«</Box>
            {comment.fullComment}
            <Box component="small" sx={{ display: "block", color: palette.inkSoft }}>Mã bình luận: {comment.commentId}</Box>
            <Box component="span" sx={{ color: palette.inkSoft, ml: "2px" }}>»</Box>
          </Typography>
        </Box>

        {/* Inline actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexShrink: 0,
          }}
        >
          <ValidationActions comment={comment} onChange={onValidate} />
          <Tooltip title="Xác nhận lựa chọn hiện tại" placement="top">
            <IconButton
              size="small"
              onClick={() => onValidate(comment.action)}
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                color: palette.sage,
                border: `1px solid ${palette.sage}55`,
                backgroundColor: palette.sageBg,
                "&:hover": {
                  backgroundColor: palette.sage,
                  color: palette.surface,
                },
              }}
            >
              <Check size={16} strokeWidth={2} />
            </IconButton>
          </Tooltip>
          {onShowHistory && (
            <Box
              className="row-actions"
              sx={{ opacity: 0, transition: "opacity 140ms ease" }}
            >
              <Tooltip title="Xem lịch sử của người dùng" placement="top">
                <IconButton
                  size="small"
                  onClick={onShowHistory}
                  sx={{
                    width: 32,
                    height: 32,
                    color: palette.inkSoft,
                    "&:hover": { color: palette.ink, backgroundColor: palette.sageBg },
                  }}
                >
                  <History size={15} strokeWidth={1.6} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>
    </motion.div>
  );
}
